/** Owns the optional Possible Matches modal flow using one enhanced API path; no legacy comparison or match percentages. */
import { mapActions } from "vuex"
import eventBus from "../../utils/eventBus"

export default {
  methods: {
    ...mapActions(["getBroadcast", "getBroadcastPossibleMatches", "updateUserPossibleMatched"]),
    /**
     * Opens ranked matches after a successful source and matching response.
     * @param {number|string} broadcastId Source broadcast identity.
     * @param {boolean} [isFromOwnBroadcasts=false] Explicit own-broadcast request bypasses the suggestion opt-out.
     * @returns {Promise<void>} Updates modal state on success; optional matching failures stay closed.
     */
    async loadPossibleMatches(broadcastId, isFromOwnBroadcasts = false) {
      // Respect suggestion preferences while allowing an explicit request from own broadcasts.
      if (this.user.possibleMatchesDisabled && !isFromOwnBroadcasts) return
      this.possibleMatches.doShow = false

      // Check the source before using its metadata; unavailable optional matches never break composing.
      try {
        const source = await this.getBroadcast(broadcastId)
        if (source.status !== 200 || !source.broadcast) return
        const matches = await this.getBroadcastPossibleMatches({ broadcastId })
        if (matches.status !== 200) return

        // Keep complete rows in server rank order, without an old-results section.
        this.possibleMatches = { doShow: true, broadcastId, broadcast: source.broadcast, results: matches.payload.results.slice(0, 10) }
      } catch (error) {
        // The API logs retrieval errors; recovery restores that path without a legacy retry.
        console.error("Error loading possible broadcast matches:", error)
      }
    },
  },
  /** Initializes the existing parent-owned modal state. @returns {Object} Closed modal state. */
  data() {
    // Parent screens expose this state to the modal through their existing composition.
    return { possibleMatches: { doShow: false, broadcastId: null, broadcast: null } }
  },
  /** Connects existing own-broadcast and send events to the matching flow. @returns {void} */
  mounted() {
    // Explicit own-broadcast requests bypass preferences; new sends respect them.
    this.possibleMatchOwnListener = id => this.loadPossibleMatches(id, true)
    this.possibleMatchSendListener = ({ id }) => this.loadPossibleMatches(id)
    eventBus.$on("showPossibleMatchesFromOwnBroadcast", this.possibleMatchOwnListener)
    eventBus.$on("broadcastSend", this.possibleMatchSendListener)
  },
  /** Releases this screen's listeners when leaving it. @returns {void} */
  beforeDestroy() {
    // Unsubscribe only our handlers so navigation cannot accumulate matching requests.
    eventBus.$off("showPossibleMatchesFromOwnBroadcast", this.possibleMatchOwnListener)
    eventBus.$off("broadcastSend", this.possibleMatchSendListener)
  },
  watch: {
    /** Persists the existing suggestion preference. @param {boolean} disabled Opt-out state. @returns {Promise<Object>} Update response. */
    "user.possibleMatchesDisabled"(disabled) {
      // Keep preference persistence separate from matching index selection.
      return this.updateUserPossibleMatched(disabled)
    },
  },
}
